import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "bg-white w-full text-gray-600 divide-y" }
const _hoisted_3 = { class: "text-black" }
const _hoisted_4 = { class: "p-6 text-left font-normal" }
const _hoisted_5 = { class: "p-6 text-left font-normal" }
const _hoisted_6 = { class: "p-6 text-left font-normal" }
const _hoisted_7 = { class: "p-6 text-left font-normal" }
const _hoisted_8 = { class: "p-6" }
const _hoisted_9 = { class: "p-6 text-green-600" }
const _hoisted_10 = { class: "p-6" }
const _hoisted_11 = { class: "p-6" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 0,
  class: "mt-6"
}
const _hoisted_15 = ["src", "title"]

import { onBeforeUnmount, ref } from "vue";
import { DocumentDataService } from "@/services";
import { t } from "@/i18n";
import { formatDateTime } from "@/utils/date";
import PdfViewer from "@/components/common/PdfViewer.vue";

interface AttachmentFile {
  creator: string;
  guid: string;
  filename: string;
  createdAt: string;
  fileType: string;
  ext?: string;
  isElectronicCopy?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  props: {
    files: null,
    documentId: null
  },
  setup(__props: any) {

const props = __props as {
  files: AttachmentFile[];
  documentId?: string | null;
}



const previewOpen = ref(false);
const previewBlob = ref<Blob | null>(null);
const previewTitle = ref("");
const desktopPreviewUrl = ref("");
const desktopPreviewTitle = ref("");
const previewLoading = ref(false);
const publicSignsUrl = ref<string | null>(null);
const previewableMimeTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const normalizeExtension = (ext?: string) => {
  const value = (ext || "").trim().toLowerCase();
  return value && !value.startsWith(".") ? `.${value}` : value;
};

const canPreview = (fileType: string, ext?: string) =>
  previewableMimeTypes.includes(fileType) ||
  [".pdf", ".doc", ".docx"].includes(normalizeExtension(ext));

const isWordFile = (file: AttachmentFile) =>
  [".doc", ".docx"].includes(normalizeExtension(file.ext)) ||
  [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ].includes(file.fileType);

const resolveQrUrl = async () => {
  if (publicSignsUrl.value) {
    return publicSignsUrl.value;
  }
  if (props.documentId == null || props.documentId === "") {
    return null;
  }
  try {
    const response = await DocumentDataService.getPublicSignsUrl(
      props.documentId
    );
    const url = response?.data?.data?.url;
    if (url) {
      publicSignsUrl.value = url;
      return url;
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

const showFile = async (file: AttachmentFile) => {
  if (!canPreview(file.fileType, file.ext)) {
    return;
  }
  if (previewLoading.value) return;
  previewLoading.value = true;
  try {
    const qrUrl = file.isElectronicCopy ? null : await resolveQrUrl();
    const response = await DocumentDataService.downloadDocument(file.guid, qrUrl);
    const contentDisposition = String(
      response.headers?.["content-disposition"] || ""
    ).toLowerCase();
    const responseIsPdf =
      contentDisposition.includes(".pdf") ||
      file.fileType === "application/pdf" ||
      normalizeExtension(file.ext) === ".pdf" ||
      (Boolean(qrUrl) && isWordFile(file));
    const blob = new Blob([response.data], {
      type: responseIsPdf
        ? "application/pdf"
        : file.fileType || "application/octet-stream",
    });
    if (isMobilePreview()) {
      clearDesktopPreview();
      previewBlob.value = blob;
      previewTitle.value = file.filename;
      previewOpen.value = true;
      return;
    }
    closePreview();
    clearDesktopPreview();
    desktopPreviewUrl.value = URL.createObjectURL(blob);
    desktopPreviewTitle.value = file.filename;
  } catch (error) {
    console.error(error);
  } finally {
    previewLoading.value = false;
  }
};

const closePreview = () => {
  previewOpen.value = false;
  previewBlob.value = null;
  previewTitle.value = "";
};

const clearDesktopPreview = () => {
  if (desktopPreviewUrl.value) URL.revokeObjectURL(desktopPreviewUrl.value);
  desktopPreviewUrl.value = "";
  desktopPreviewTitle.value = "";
};

const isMobilePreview = () =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
  window.matchMedia("(max-width: 768px)").matches;

const downloadDocument = async (file: AttachmentFile) => {
  try {
    const qrUrl = file.isElectronicCopy ? null : await resolveQrUrl();
    const response = await DocumentDataService.downloadDocument(file.guid, qrUrl);
    const blob = new Blob([response.data], { type: file.fileType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = file.filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
  }
};

onBeforeUnmount(() => {
  closePreview();
  clearDesktopPreview();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "table-scroll",
      style: {"--table-min-width":"760px"},
      tabindex: "0",
      role: "region",
      "aria-label": _ctx.$t('common.scrollableTable')
    }, [
      _createElementVNode("table", _hoisted_2, [
        _createElementVNode("tr", _hoisted_3, [
          _createElementVNode("th", _hoisted_4, _toDisplayString(_unref(t)("fileTable.owner")), 1),
          _createElementVNode("th", _hoisted_5, _toDisplayString(_unref(t)("fileTable.fileName")), 1),
          _createElementVNode("th", _hoisted_6, _toDisplayString(_unref(t)("fileTable.uploadedAt")), 1),
          _createElementVNode("th", _hoisted_7, _toDisplayString(_unref(t)("fileTable.file")), 1)
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.files, (file) => {
          return (_openBlock(), _createElementBlock("tr", {
            key: file.guid
          }, [
            _createElementVNode("td", _hoisted_8, _toDisplayString(file.creator), 1),
            _createElementVNode("td", _hoisted_9, _toDisplayString(file.filename), 1),
            _createElementVNode("td", _hoisted_10, _toDisplayString(_unref(formatDateTime)(file.createdAt)), 1),
            _createElementVNode("td", _hoisted_11, [
              (canPreview(file.fileType, file.ext))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: "#",
                    onClick: _withModifiers(($event: any) => (showFile(file)), ["prevent"]),
                    class: "no-underline text-green-600 mr-4"
                  }, _toDisplayString(_unref(t)("fileTable.showDocument")), 9, _hoisted_12))
                : _createCommentVNode("", true),
              _createElementVNode("a", {
                href: "#",
                target: "_blank",
                onClick: _withModifiers(($event: any) => (downloadDocument(file)), ["prevent"]),
                class: "no-underline text-green-600"
              }, _toDisplayString(_unref(t)("fileTable.downloadFile")), 9, _hoisted_13)
            ])
          ]))
        }), 128))
      ])
    ], 8, _hoisted_1),
    (desktopPreviewUrl.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("iframe", {
            width: "100%",
            height: "1000px",
            src: desktopPreviewUrl.value,
            title: desktopPreviewTitle.value
          }, null, 8, _hoisted_15)
        ]))
      : _createCommentVNode("", true),
    _createVNode(PdfViewer, {
      show: previewOpen.value,
      source: previewBlob.value,
      title: previewTitle.value,
      onClose: closePreview
    }, null, 8, ["show", "source", "title"])
  ], 64))
}
}

})